import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '~/lib/utils';

const buttonVariants = {
  variantStyles: {
    default: 'bg-foreground text-white',
    icon: 'bg-input-primary rounded-lg w-10 h-10 p-0',
    iconDark: 'bg-[#312e3b] rounded-lg w-10 h-10 p-0',
    primary: 'bg-primary',
    light: 'bg-white',
    ghost: 'bg-transparent',
    link: 'bg-transparent',
    linkSecondary: 'bg-transparent',
  },
  sizeStyles: {
    default: 'px-4 py-2',
    sm: 'px-3 py-1.5',
    lg: 'px-6 py-3',
    icon: 'p-0',
  },
  textVariant: {
    default: 'text-white' as const,
    light: '' as const,
    primary: 'text-white' as const,
    ghost: '' as const,
    icon: '' as const,
    iconDark: 'text-white' as const,
    link: 'text-link' as const,
    linkSecondary: 'text-link-secondary' as const,
  },
};

interface ButtonProps extends React.ComponentProps<'button'> {
  asChild?: boolean;
  loading?: boolean;
  caption?: string;
  variant?:
    | 'default'
    | 'ghost'
    | 'icon'
    | 'iconDark'
    | 'primary'
    | 'light'
    | 'link'
    | 'linkSecondary';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  caption,
  children,
  disabled = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  const baseStyles = 'flex flex-row items-center justify-center rounded-full font-bold';
  const disabledStyles = disabled || loading ? 'opacity-50' : '';

  return (
    <Comp
      data-slot="button"
      className={cn(
        baseStyles,
        buttonVariants.variantStyles[variant],
        buttonVariants.sizeStyles[size],
        buttonVariants.textVariant[variant],
        className,
        disabledStyles
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {caption && <span className="ml-2">{caption}</span>}
          {children}
        </>
      ) : (
        <>
          {caption && <span className="ml-2">{caption}</span>}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
